import { useCallback, useEffect, useMemo, useState } from 'react';

import type { EntityType } from '@einhaus/project314-types';

import { nanoid } from 'nanoid';

import type { OptimizerBundlePreviewMetrics } from '@/types/appRouter';
import type { EditorGroupNode, ScreenerGroupInput } from '../types/screenerEditor';

export type ScreenerExperiment = {
    id: string;
    createdAt: string;
    label: string;
    note: string | null;
    starred: boolean;
    jobId: number;
    ruleSummary: string;
    metrics: OptimizerBundlePreviewMetrics;
    config: {
        name: string;
        description: string;
        isActive: boolean;
        entityTypes: EntityType[];
        rootGroup: EditorGroupNode;
        alwaysIncludeSymbols: string;
        rootGroupInput: ScreenerGroupInput;
    };
    source: {
        screenerRuleId: number;
        screenerRootId: number;
    };
};

const STORAGE_VERSION = 1 as const;
type StorageShapeV1 = { version: typeof STORAGE_VERSION; experiments: ScreenerExperiment[] };

function storageKeyForScreenerRootId(screenerRootId: number): string {
    return `p314:screenerExperiments:v${STORAGE_VERSION}:${screenerRootId}`;
}

function safeParseStorage(raw: string): StorageShapeV1 | null {
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== 'object') return null;

        const { version } = parsed as { version?: unknown };
        if (version !== STORAGE_VERSION) return null;

        const { experiments } = parsed as { experiments?: unknown };
        if (!Array.isArray(experiments)) return { version: STORAGE_VERSION, experiments: [] };

        return { version: STORAGE_VERSION, experiments: experiments as ScreenerExperiment[] };
    } catch {
        return null;
    }
}

export function createScreenerExperimentId(): string {
    return nanoid(12);
}

export function useScreenerExperiments(screenerRootId: number | null) {
    const storageKey = useMemo(() => (screenerRootId ? storageKeyForScreenerRootId(screenerRootId) : null), [screenerRootId]);
    const [experiments, setExperiments] = useState<ScreenerExperiment[]>([]);

    useEffect(() => {
        if (!storageKey) {
            setExperiments([]);
            return;
        }

        const raw = window.localStorage.getItem(storageKey);
        const parsed = raw ? safeParseStorage(raw) : null;
        setExperiments(parsed?.experiments ?? []);
    }, [storageKey]);

    useEffect(() => {
        if (!storageKey) return;
        const payload: StorageShapeV1 = { version: STORAGE_VERSION, experiments };
        window.localStorage.setItem(storageKey, JSON.stringify(payload));
    }, [experiments, storageKey]);

    useEffect(() => {
        if (!storageKey) return;

        const handleStorage = (e: StorageEvent) => {
            if (e.key !== storageKey) return;
            const parsed = e.newValue ? safeParseStorage(e.newValue) : null;
            setExperiments(parsed?.experiments ?? []);
        };

        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, [storageKey]);

    const addExperiment = useCallback((exp: ScreenerExperiment) => {
        setExperiments((prev) => [exp, ...prev].slice(0, 200));
    }, []);

    const updateExperiment = useCallback((id: string, patch: Partial<ScreenerExperiment>) => {
        setExperiments((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
    }, []);

    const deleteExperiment = useCallback((id: string) => {
        setExperiments((prev) => prev.filter((e) => e.id !== id));
    }, []);

    const clearExperiments = useCallback(() => {
        setExperiments([]);
    }, []);

    return { experiments, addExperiment, updateExperiment, deleteExperiment, clearExperiments };
}
